/**
 * PATIENT HISTORY PANEL - Displays patient intake (history) data in the right sidebar.
 * Data comes from the history form filled by the patient (patient_intake).
 */

import React, { useState, useEffect } from 'react';
import { getPatientIntake, type PatientIntake } from '../api/patientIntake';

function getAge(dateOfBirth: string | null | undefined): string {
  if (!dateOfBirth) return '—';
  const d = new Date(dateOfBirth);
  if (isNaN(d.getTime())) return '—';
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 ? `${age}` : '—';
}

function buildConditionsList(i: PatientIntake): string[] {
  const labels: [keyof PatientIntake, string][] = [
    ['hypertension', 'Hypertension'],
    ['high_cholesterol', 'High cholesterol'],
    ['cad', 'CAD'],
    ['heart_failure', 'Heart failure'],
    ['arrhythmia', 'Arrhythmia'],
    ['stroke', 'Stroke'],
    ['asthma', 'Asthma'],
    ['copd', 'COPD'],
    ['sleep_apnea', 'Sleep apnea'],
    ['chronic_bronchitis', 'Chronic bronchitis'],
    ['diabetes', 'Diabetes'],
    ['ckd', 'CKD'],
    ['thyroid', 'Thyroid'],
    ['obesity', 'Obesity'],
    ['depression_anxiety', 'Depression/Anxiety'],
  ];
  const list: string[] = [];
  for (const [key, label] of labels) {
    if (i[key] === true) list.push(label);
  }
  if (i.other_lung_disease) list.push(`Other lung: ${i.other_lung_disease}`);
  if (i.other_condition) list.push(i.other_condition);
  return list;
}

const PatientHistoryPanel: React.FC<{ patId?: string; onNavigateToHistory?: () => void }> = ({ patId, onNavigateToHistory }) => {
  const [intake, setIntake] = useState<PatientIntake | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!patId) {
      setIntake(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPatientIntake(patId)
      .then((data) => {
        if (!cancelled) {
          setIntake(data);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [patId]);

  const display = (v: string | number | boolean | null | undefined): string => {
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    if (v == null || v === '') return '—';
    return String(v);
  };

  const conditions = intake ? buildConditionsList(intake) : [];

  return (
    <div className="patient-history-panel" style={{ width: '100%', fontSize: '20px', color: '#222' }}>
      {!patId && (
        <div style={{ padding: '8px', color: '#6c757d' }}>Select a patient to view history.</div>
      )}
      {patId && loading && (
        <div style={{ padding: '8px', color: '#6c757d' }}>Loading...</div>
      )}
      {patId && error && (
        <div style={{ padding: '8px', color: '#dc3545' }}>{error}</div>
      )}
      {patId && !loading && !error && intake === null && (
        <div style={{
          padding: '12px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          <p style={{ margin: 0, color: '#495057', fontSize: '20px' }}>
            No intake recorded for this patient yet. You can add one now.
          </p>
          <button
            type="button"
            onClick={() => onNavigateToHistory?.()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '18px',
              alignSelf: 'flex-start',
            }}
          >
            Add intake
          </button>
        </div>
      )}
      {patId && !loading && !error && intake !== null && (
        <div style={{
          padding: '6px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          maxHeight: '250px',
          overflowY: 'auto',
        }}>
          {/* Anonymous avatar (Instagram-style: white bg + gray silhouette) */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: '#fafafa',
              border: '1px solid #efefef',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
            }}>
              <svg width="52" height="52" viewBox="0 0 24 24" fill="#8e8e8e" aria-hidden="true">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, flex: 1 }}>
              <div><span style={{ color: '#6c757d' }}>Age </span>{getAge(intake?.date_of_birth)}</div>
              <div><span style={{ color: '#6c757d' }}>Phone </span>{display(intake?.phone)}</div>
              <div style={{ wordBreak: 'break-all' }}><span style={{ color: '#6c757d' }}>Email </span>{display(intake?.email)}</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ color: '#6c757d', fontSize: '18px' }}>Reason</span>
              <span>{display(intake?.reason)}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ color: '#6c757d', fontSize: '18px' }}>Complaint</span>
              <span style={{ whiteSpace: 'pre-wrap' }}>{display(intake?.complaint)}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ color: '#6c757d', fontSize: '18px' }}>Conditions</span>
              <span>{conditions.length === 0 ? '—' : conditions.join(', ')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientHistoryPanel;
